import { Component, Input, ViewChild, ElementRef, EventEmitter, Output, } from '@angular/core';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { validateEmail } from '../../../Misc/helpers/formatting/email.helper';

enum EmailIcons {
    exclamation = 'fa-regular fa-duotone fa-circle-exclamation',
    check = 'fa-regular fa-duotone fa-circle-check'
}

@Component({
    selector: 'agpr-email-text-input',
    templateUrl: './email-text-input.component.html',
    styleUrls: ['./email-text-input.component.scss'],
    providers: [getProvider(EmailTextInputComponent)],
    standalone: false
})
export class EmailTextInputComponent extends ControlValueAccessorModel<string> {
    @Input() public id: string;
    @Input() public placeholder: string;
    @Input() public label: string;
    @Input() public tooltip: string;
    @Input() public required: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public ariaLabel: string = '';

    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() keyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() emailChange = new EventEmitter<{
        isValid: boolean;
        elementRef: ElementRef;
    }>();

    @ViewChild('emailInput', { read: ElementRef }) emailInputRef: ElementRef;
    @ViewChild('ariaLiveRegion') ariaLiveRegion: ElementRef;
    emailIcon: EmailIcons | null = null;
    emailHasError: boolean = false;
    assessmentMessages: string[] = [];

    private readonly noReplyEmail: string[] = ['no-reply@', 'noreply@', 'do-not-reply@', 'donotreply@'];

    constructor(public localizationService: LocalizationService){
        super();
    }

    writeValue(value: string): void {
        this.value = value;
    }

    onKeydown(event: KeyboardEvent) {
        this.keydown.emit(event);
    }

    onKeyUp(event: KeyboardEvent) {
        this.keyup.emit(event);
    }

    onBlur(event: FocusEvent): void {
        this.blur.emit(event);
    }

    assessEmail(){
        const messages: string[] = [];
        const currentlyHasError = this.emailHasError;
        this.emailHasError = false;
        (this.ariaLiveRegion.nativeElement as HTMLElement).textContent = '';
        if (!this.value || this.value.length === 0) {
            this.emailIcon = null;
        } else {
            if (!validateEmail(this.value)) {
                this.emailHasError = true;
                this.emailIcon = null;
            } else if (this.noReplyEmail.find(x => this.value.startsWith(x))) {
                messages.push(this.localizationService.transform('Discover.userPreferences.defaultDetails.to.warning.noReply'));
                this.emailIcon = EmailIcons.exclamation;
                (this.ariaLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform('Common.input.showWarningMessage');
            } else {
                this.emailIcon = EmailIcons.check;
            }
        }

        const errorChanged = currentlyHasError !== this.emailHasError;

        if (this.emailInputRef && errorChanged) {
            this.emailChange.emit({
                isValid: !this.emailHasError,
                elementRef: this.emailInputRef
            });
        }
        this.assessmentMessages = messages;
    }
}
